import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { AuthProvider, accueilPourRole, useRoleCourant } from './auth/AuthContext';
import { LoginScreen } from './auth/LoginScreen';
import { RequireAuth } from './auth/RequireAuth';
import { WelcomePage } from './auth/WelcomePage';
import { Shell } from './components/Shell';
import { AdministrationPage } from './pages/AdministrationPage';
import { AtelierExercicePage } from './pages/AtelierExercicePage';
import { AujourdhuiAdminPage } from './pages/AujourdhuiAdminPage';
import { ComposerParcoursPage } from './pages/ComposerParcoursPage';
import { ComposerSequencePage } from './pages/ComposerSequencePage';
import { ContenusPage } from './pages/ContenusPage';
import { CorpusPage } from './pages/CorpusPage';
import { CopieAnnoteePage, CopiesElevePage } from './pages/CopiesElevePage';
import { DetailElevePage } from './pages/DetailElevePage';
import { EditeurFigurePage } from './pages/EditeurFigurePage';
import { ExerciceCiblePage, ExercicesPage } from './pages/ExerciceCiblePage';
import { MaJourneePage } from './pages/MaJourneePage';
import { MaClassePage } from './pages/MaClassePage';
import { ManuelElevePage } from './pages/ManuelElevePage';
import { MesElevesPage } from './pages/MesElevesPage';
import { MonComptePage } from './pages/MonComptePage';
import { PreparationSeancePage } from './pages/PreparationSeancePage';
import { ChapitreProgrammePage, ProgrammePage } from './pages/ProgrammePage';
import {
  CapsuleRelecturePage,
  ErreurRelecturePage,
  FicheRelecturePage,
} from './pages/RelectureSavoirPages';
import { RapportPage, RapportsPage } from './pages/RapportPage';
import { RapportUniquePage } from './pages/RapportUniquePage';
import { RelectureExercicesPage } from './pages/RelectureExercicesPage';
import { RevueEvaluationPage } from './pages/RevueEvaluationPage';
import { SuiviParcoursClassePage } from './pages/SuiviParcoursClassePage';
import { ValidationsPage } from './pages/ValidationsPage';

/**
 * Routes de la console tuteur / admin.
 *
 * L'ENTRÉE EST « MA JOURNÉE » (T-1), pas le tableau des élèves : la console
 * ouvre sur une file de travail déjà priorisée, bornée et clôturable. Le
 * tableau « Mes élèves » reste, en deuxième rang, pour la consultation.
 *
 * Il n'y a aucune route de messagerie, et il n'y en aura pas : Silex ne
 * transporte aucun message, ni tuteur↔élève ni tuteur↔parent.
 */
/**
 * `/ma-journee` est la file du TUTEUR : `/v1/tuteur/file` refuse un
 * administrateur, et l'écran restait sur « Chargement » sans fin (audit,
 * « mineurs »). L'administrateur est renvoyé à SON accueil (L1).
 */
function JourneeOuAccueil() {
  const role = useRoleCourant();
  if (role === 'admin') return <Navigate to={accueilPourRole('admin')} replace />;
  return <MaJourneePage />;
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginScreen />} />

          <Route
            element={
              <RequireAuth>
                <Shell />
              </RequireAuth>
            }
          >
            {/* T-1 — l'écran d'entrée. */}
            <Route path="/ma-journee" element={<JourneeOuAccueil />} />

            {/* Le tableau de consultation, et la fiche élève (T-3). */}
            <Route path="/eleves" element={<MesElevesPage />} />

            {/* La classe : un rangement des élèves DÉJÀ suivis. L'accès, lui,
                vient du code de suivi (cf. `MesElevesPage`). */}
            <Route path="/classes" element={<MaClassePage />} />
            {/* Lot P3 (AB#975) — le parcours : le composer, pour un élève ou une
                classe (`?eleve=` ou `?classe=`), et suivre une classe. Le suivi
                d'un élève vit dans sa fiche. Mêmes gardes que le manuel. */}
            <Route path="/classes/:id/parcours" element={<SuiviParcoursClassePage />} />
            <Route path="/parcours/nouveau" element={<ComposerParcoursPage />} />
            <Route path="/eleves/:id" element={<DetailElevePage />} />

            {/* Les travaux corrigés d'un élève, et la copie annotée elle-même.
                Le fondateur, en recette : « impossible de charger les devoirs
                annotés pour les visualiser. On voit juste la liste. » La console
                n'avait aucune route vers les copies — celle-ci est la page
                manquante. */}
            <Route path="/eleves/:eleveId/copies" element={<CopiesElevePage />} />
            <Route path="/eleves/:eleveId/copies/:copieId" element={<CopieAnnoteePage />} />

            {/* Chantier « manuel vivant », Φ-MV-5 — la fiche d'un élève dans son
                chapitre : son objectif, son portrait, ses séquences, ses séances.
                UNE ADRESSE PAR ONGLET (MV-TUT-02), par deux routes plutôt qu'un
                segment optionnel, comme l'administration ; « composer » est une
                sous-page, déclarée avant l'onglet pour ne pas être lue comme un.
                Les pages se gardent elles-mêmes : drapeau éteint, elles
                renvoient à l'accueil sans rien demander au serveur. */}
            <Route path="/eleves/:id/manuel" element={<ManuelElevePage />} />
            <Route path="/eleves/:id/manuel/composer" element={<ComposerSequencePage />} />
            {/* Seconde tranche (MV-TUT-04, MV-TUT-11) — deux LECTURES LONGUES, donc
                deux sous-pages et non deux onglets : préparer la séance, et le
                rapport que la famille reçoit. Déclarées, comme « composer », avant
                l'onglet. « Travaux et corrections » et « Évolution », eux, sont
                des onglets : ils passent par la route d'en dessous. */}
            <Route path="/eleves/:id/manuel/preparation" element={<PreparationSeancePage />} />
            <Route path="/eleves/:id/manuel/rapport" element={<RapportUniquePage />} />
            <Route path="/eleves/:id/manuel/:onglet" element={<ManuelElevePage />} />

            {/* T-2 — la file de validation, puis un item à la fois. */}
            <Route path="/validation" element={<ValidationsPage />} />
            <Route path="/validation/:evaluationId" element={<RevueEvaluationPage />} />

            {/* T-4 — cibler un exercice (les étapes 2 à 4 ne sont pas construites). */}
            <Route path="/exercices" element={<ExercicesPage />} />
            <Route path="/exercices/nouveau" element={<ExerciceCiblePage />} />

            {/* Sprint 24 — la banque de savoirs. Tuteur ET admin : le tuteur
                voit les items échouer en séance et sait pourquoi ; l'admin
                répond des plafonds du corpus. */}
            <Route path="/corpus" element={<CorpusPage />} />
            {/* L'atelier d'écriture — concevoir un exercice pour un savoir, et
                non pour un élève : c'est ce qui le distingue d'« Exercices
                ciblés », qui part d'une unité de la file du matin. */}
            <Route path="/atelier" element={<AtelierExercicePage />} />

            {/* Chantier « manuel vivant », Φ-MV-1 — la console lit un chapitre :
                notions, prérequis, attentes du programme, réemplois. Les deux
                pages se gardent elles-mêmes : drapeau éteint (build ou
                capacité), elles renvoient à l'accueil sans rien demander au
                serveur, et l'entrée « Programme » n'est pas au menu. */}
            <Route path="/programme" element={<ProgrammePage />} />
            <Route path="/programme/:code" element={<ChapitreProgrammePage />} />
            {/* Φ-MV-2 — relire une fiche, une capsule, une erreur et son remède.
                Une adresse chacune (lecture longue = sous-page) ; même garde
                que le chapitre dont elles relèvent. */}
            <Route path="/programme/:code/fiches/:fiche" element={<FicheRelecturePage />} />
            <Route path="/programme/:code/capsules/:capsule" element={<CapsuleRelecturePage />} />
            <Route path="/programme/:code/erreurs/:erreur" element={<ErreurRelecturePage />} />
            {/* AB#747 — tout compte connecté : identité, mot de passe, double facteur. */}
            <Route path="/mon-compte" element={<MonComptePage />} />

            {/* T-6 — le rapport, seul canal du tuteur vers la famille. */}
            <Route path="/rapports" element={<RapportsPage />} />
            <Route path="/rapports/:eleveId" element={<RapportPage />} />

            {/* UNE ADRESSE PAR ONGLET (14 septembre 2026). L'onglet actif vivait
                dans un état local : impossible d'envoyer un lien vers
                « Affectations », de revenir en arrière, ou de retrouver son
                onglet après un rechargement — chaque retour repartait de
                « Consommation IA », l'écran dont on a le moins besoin.
                Deux routes plutôt qu'un segment optionnel : même effet, et
                aucune dépendance à une version de routeur. */}
            {/* Refonte de la console, L1 (15 octobre 2026) — le menu par tâches.
                Quatre adresses NEUVES ; aucune ancienne n'est retirée.
                « Aujourd'hui » de l'administrateur ; « Contenus », le manuel
                chapitre par chapitre ; « Comptes » et « Plateforme », qui
                regroupent les onglets de `/administration` (toujours servie). */}
            <Route
              path="/aujourdhui"
              element={
                <RequireAuth adminOnly>
                  <AujourdhuiAdminPage />
                </RequireAuth>
              }
            />
            <Route path="/contenus" element={<ContenusPage />} />
            {/* L3 — la file de relecture des exercices d'un chapitre. Tuteur ET
                admin (D-4) : relire n'est pas publier. */}
            <Route path="/contenus/:code/exercices" element={<RelectureExercicesPage />} />
            {/* Atelier de figures, L7 (AB#943) — l'éditeur de figure : construire,
                proposer les contraintes, sortir le JSON du chapitre. N'écrit rien
                au serveur ; aucun modèle. */}
            <Route path="/contenus/editeur-figure" element={<EditeurFigurePage />} />
            {(['comptes', 'plateforme'] as const).flatMap((section) => [
              <Route
                key={section}
                path={`/${section}`}
                element={
                  <RequireAuth adminOnly>
                    <AdministrationPage section={section} />
                  </RequireAuth>
                }
              />,
              <Route
                key={`${section}-onglet`}
                path={`/${section}/:onglet`}
                element={
                  <RequireAuth adminOnly>
                    <AdministrationPage section={section} />
                  </RequireAuth>
                }
              />,
            ])}

            <Route
              path="/administration"
              element={
                <RequireAuth adminOnly>
                  <AdministrationPage />
                </RequireAuth>
              }
            />
            <Route
              path="/administration/:onglet"
              element={
                <RequireAuth adminOnly>
                  <AdministrationPage />
                </RequireAuth>
              }
            />
          </Route>

          <Route path="/" element={<WelcomePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
